import {StyleSheet, type CSSProperties} from "aphrodite";
import * as React from "react";
import {useCallback, useEffect, useState} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {MediaLayout} from "@khanacademy/wonder-blocks-layout";
// eslint-disable-next-line no-restricted-imports -- darkBlue is still required
import {color, semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {usePrefersReducedMotion} from "@khan/data-prefers-reduced-motion";
import {staticUrl} from "@khan/urls";

// A simple component for rendering the background elements in the page.
// We don't use `addStyle` here as it ends up being too slow to update quickly
const BackgroundImg = ({
    src,
    style,
    ...props
}: {
    src: string;
    style?: CSSProperties;
}) => (
    <img
        {...props}
        src={staticUrl(src)}
        alt=""
        aria-hidden={true}
        style={{position: "absolute", ...style}}
    />
);

// amount to shift full background down from the top.
const TOP_OFFSET = spacing.xxLarge_48;

interface Props {
    children: React.ReactNode;
}

export default function NotFoundBackground({children}: Props) {
    const prefersReducedMotion = usePrefersReducedMotion();
    const [mouseX, setMouseX] = useState(700);
    const [mouseY, setMouseY] = useState(400);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        setMouseX(e.clientX);
        setMouseY(e.clientY);
    }, []);

    useEffect(() => {
        if (!prefersReducedMotion) {
            document.addEventListener("mousemove", handleMouseMove);
            return () =>
                document.removeEventListener("mousemove", handleMouseMove);
        }
    }, [prefersReducedMotion, handleMouseMove]);

    const small = (
        <BackgroundImg
            style={{
                // 50% of the viewport - half the width of the image
                insetInlineStart: "calc(50vw - 125px)",
                bottom: 0,
            }}
            src="/images/404/mobile_dog_planet.svg"
        />
    );

    const leftParallax = (mouseX - 700) * 0.1;
    const topParallax = (mouseY - 400) * 0.1;

    const mdOrLarger = (
        <>
            <BackgroundImg
                style={{
                    insetInlineStart: 105 + leftParallax * 0.8,
                    top: TOP_OFFSET + 33 + topParallax * 0.8,
                }}
                src="/images/404/little_stars.svg"
            />
            <BackgroundImg
                style={{
                    insetInlineStart: 648 + leftParallax * 0.6,
                    top: TOP_OFFSET + 113 + topParallax * 0.6,
                }}
                src="/images/404/large_stars.svg"
            />
            <BackgroundImg
                style={{
                    insetInlineStart: 855 + leftParallax * 0.2,
                    top: TOP_OFFSET + 58 + topParallax * 0.2,
                }}
                src="/images/404/question_marks.svg"
            />
            <BackgroundImg
                style={{
                    insetInlineStart: -100 + leftParallax * 0.02,
                    top: 563 + topParallax * 0.02,
                }}
                src="/images/404/mountain.svg"
            />
            <BackgroundImg
                style={{
                    insetInlineStart: 474 + leftParallax * 0.2,
                    top: TOP_OFFSET + 301 + topParallax * 0.2,
                }}
                src="/images/404/planet.svg"
            />
            <BackgroundImg
                style={{
                    insetInlineStart: 911 + leftParallax * 0.2,
                    top: TOP_OFFSET + 147 + topParallax * 0.2,
                }}
                src="/images/404/dog_2.svg"
            />
        </>
    );

    return (
        <MediaLayout styleSheets={styleSheets}>
            {({mediaSize, styles}) => (
                <View style={styles.rootBackground}>
                    <View style={styles.rootStyle}>
                        <View style={styles.background}>
                            {mediaSize === "small" ? small : mdOrLarger}
                        </View>
                        <View style={styles.content}>{children}</View>
                    </View>
                </View>
            )}
        </MediaLayout>
    );
}

const styleSheets = {
    all: StyleSheet.create({
        rootBackground: {
            background: color.darkBlue,
            color: semanticColor.core.foreground.knockout.default,
            overflow: "hidden",
        },

        rootStyle: {
            flex: 1,
        },

        background: {
            position: "absolute",
            width: "100%",
            height: "100%",
        },

        content: {
            marginInlineStart: spacing.xxxLarge_64,
            marginBlockStart: TOP_OFFSET + spacing.xxxLarge_64,
            // keeps the text comfortably over the blue part of the background
            maxWidth: 420,
        },
    }),

    mdOrLarger: StyleSheet.create({
        rootStyle: {
            // The width of all the background images laid out, it's ok if
            // it's wider than the viewport as it overflows
            width: 1450,
        },

        rootBackground: {
            minBlockSize: "100vh",
            paddingBlockEnd: 102,
        },
    }),

    small: StyleSheet.create({
        rootBackground: {
            // A good height to show both the text and the mobile dog
            height: 600,
        },

        content: {
            marginInlineStart: spacing.small_12,
            marginBlockStart: spacing.xxxLarge_64,
            maxWidth: 300,
        },
    }),

    medium: StyleSheet.create({
        // eslint-disable-next-line react-native/no-unused-styles -- is actually used
        background: {
            // Line up the background better
            insetInlineStart: -200,
        },
    }),
} as const;
